import type { ReactNode } from "react";
import { CircleAlert, TriangleAlert } from "lucide-react";

import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

/**
 * The one shape every PROTEX refusal, failure and read-only notice is drawn in
 * — the mock-up's alert card: a rule and (when it is alarming) a mark, an INK
 * headline that says what happened, and a GREY line that says what it does not
 * mean.
 *
 * COMPOSED ON `ui/alert`, never beside it (AGENTS boundary 1). The first cut
 * of this component re-drew the primitive's own job in raw Tailwind — its
 * `role="alert"`, its icon-aware grid, its title/description/action slots —
 * and in doing so REPLACED `Alert` call sites that were already compliant.
 * A second alert renderer is the failure boundary 1 names: the two drift, and
 * the hand-rolled one is the one that stops getting the primitive's fixes.
 * Everything below is a SKIN on those slots — spacing, the brand radius, and
 * which of the three parts is allowed to be red.
 *
 * Red is spent on the rule and the mark only. The words stay ink and grey,
 * because what they say is usually reassurance ("nothing is missing",
 * "nothing was lost") — setting reassurance in alarm red made two cards read
 * as data loss while their copy said the opposite (outcome-audit cycles 2
 * and 3, G3 and M3). So this is deliberately NOT `variant="destructive"`,
 * which paints the description red, and the red is not put on the container
 * either: `Button`'s `outline` variant carries no colour of its own and would
 * inherit it, turning "Try again" red.
 *
 * `action` sits at the end of the row, where the eye ends the sentence. A
 * card whose action is a separate step (Reload, under the 409 card) renders
 * that button itself, below the card — see `ConflictCard`.
 */
export function AlertCard({
  title,
  detail,
  icon = "triangle",
  tone = "danger",
  action,
}: {
  title: ReactNode;
  detail?: ReactNode;
  /** The mock-up's list failure draws a triangle; its 409 card a circle. */
  icon?: "triangle" | "circle";
  /**
   * `danger` is a refusal or a failure: red rule, red mark.
   *
   * `quiet` is a card that reports NO alarm — "this value is read-only here" —
   * and so draws the same grey rule (`--ks-grey-4`) as every other card on the
   * pane and no mark at all. It exists because the alternative was a bare
   * `Alert`, whose `border` has no colour class and therefore inherits
   * Tailwind 4's `currentColor`: full ink, 1px, the loudest rule on the
   * surface sitting on its quietest message (outcome-audit cycle 6, O1). The
   * fix is a TONE on the one card component, not a border class sprinkled at
   * the call site, because the next bare `Alert` would make the same mistake.
   *
   * A mark is what separates the two: the mock-up marks what it is sounding an
   * alarm about and leaves a notice unmarked, so drawing a grey triangle here
   * would only move the contradiction rather than remove it.
   */
  tone?: "danger" | "quiet";
  action?: ReactNode;
}) {
  const marked = tone !== "quiet";
  const Mark = icon === "circle" ? CircleAlert : TriangleAlert;
  return (
    <Alert
      className={cn(
        "rounded-ks-lg px-5 py-4",
        marked
          ? [
              // `!` on the mark's colour because the primitive sets `*:[svg]:
              // text-current` on the same child selector at the same
              // specificity: which of the two wins would otherwise depend on
              // emitted stylesheet order, and a mark that silently turns ink is
              // the one part of this card that carries the alarm.
              "border-ks-danger *:[svg]:text-ks-danger! has-[>svg]:gap-x-4",
              // `minmax(0,1fr)` rather than the primitive's `1fr`, which floors
              // the track at min-content: the words column carries a fact key
              // and an area name, and one unbreakable token would push the card
              // wider than its parent. This is what `min-w-0 flex-1` was doing
              // on the flex row.
              "has-[>svg]:grid-cols-[auto_minmax(0,1fr)]",
              // The action rides in the grid as a third column rather than in
              // the primitive's absolutely-positioned slot: pinned, it would
              // need a GUESSED reserve of right padding, and a button wider
              // than the guess sits on top of the headline. That reserve — 72px
              // — is what the primitive applies on sight of the slot, so it is
              // also turned back down to the card's own `px-5` gutter.
              action &&
                "has-[>svg]:grid-cols-[auto_minmax(0,1fr)_auto] has-data-[slot=alert-action]:pr-5",
            ]
          : [
              // No mark, so none of the primitive's `has-[>svg]:` grid rules
              // fire and the words own the whole width. The grid is stated
              // anyway when there is an action, for the same reason as above.
              "border-ks-grey-4",
              action && "grid-cols-[minmax(0,1fr)_auto] has-data-[slot=alert-action]:pr-5",
            ],
      )}
    >
      {marked && <Mark aria-hidden />}
      <AlertTitle className="font-normal text-ks-ink">{title}</AlertTitle>
      {detail && (
        <AlertDescription className="text-[13px] text-ks-grey-1">{detail}</AlertDescription>
      )}
      {action && (
        <AlertAction
          className={cn(
            "static row-start-1 row-span-2 self-center",
            marked ? "col-start-3" : "col-start-2",
          )}
        >
          {action}
        </AlertAction>
      )}
    </Alert>
  );
}
